import {
  BlockTokenType,
  MarkdownNodeBlueprint,
  NodeType,
  parseInline,
} from "./markdownParser/mdDef.js";
import { createNode } from "./markdownParser/mdUtils.js";

const blockDetectorOrder = [
  NodeType.THEMATIC_BREAK,
  NodeType.FENCED_CODE,
  NodeType.TABLE,
  NodeType.COLLAPSE,
  NodeType.ORDERED_LIST,
  NodeType.UNORDERED_LIST,
  NodeType.BLOCKQUOTE,
  NodeType.HEADING,
];

const blockDetectors = blockDetectorOrder
  .map((type) => MarkdownNodeBlueprint[type]?.detector)
  .filter(Boolean);

function tokenize(markdown_) {
  const lines = markdown_.replace(/\r\n/g, "\n").split("\n");
  const tokens = [];
  let index = 0;
  let paragraphBuffer = [];

  // Create a standalone paragraph outside block elements
  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) {
      return;
    }
    tokens.push({
      type: BlockTokenType.PARAGRAPH,
      content: paragraphBuffer.join("\n"),
    });
    paragraphBuffer = [];
  };

  const tryConsumeToken = () => {
    for (const detector of blockDetectors) {
      const result = detector(lines, index);
      if (result) {
        flushParagraph();
        tokens.push(result.token);
        index = result.nextIndex;
        return true;
      }
    }
    return false;
  };

  while (index < lines.length) {
    const rawLine = lines[index];
    if (!rawLine.trim()) {
      flushParagraph();
      const hasRemainingContent = lines
        .slice(index + 1)
        .some(line => line.trim());
      if (hasRemainingContent) {
        tokens.push({
          type: BlockTokenType.PARAGRAPH,
          content: '&nbsp;',
        });
      }
      index += 1;
      continue;
    }

    if (tryConsumeToken()) {
      continue;
    }

    paragraphBuffer.push(rawLine.trim());
    index += 1;
  }

  flushParagraph();
  return tokens;
}

function buildDocumentTree(blockTokens_ = []) {
  const root = createNode(NodeType.DOCUMENT, {}, []);
  const builderHelpers = {
    tokenize,
    buildDocumentTree: (tokens) => buildDocumentTree(tokens),
  };
  for (const token of blockTokens_) {
    const handler = MarkdownNodeBlueprint[token.type];
    if (handler?.builder) {
      root.children.push(handler.builder(token, builderHelpers));
      continue;
    }
    root.children.push(
      createNode(NodeType.PARAGRAPH, {}, parseInline(token.content))
    );
  }
  return root;
}

function escapeHtml(value_) {
  return value_
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderNode(node_) {
  if (!node_) {
    return "";
  }
  const handler = MarkdownNodeBlueprint[node_.type];
  if (handler?.renderer) {
    return handler.renderer(node_, {
      renderChildren,
      escapeHtml,
      renderNode,
    });
  }
  return renderChildren(node_);
}

function renderChildren(node_) {
  if (!node_.children || node_.children.length === 0) {
    return "";
  }
  return node_.children.map((child_) => renderNode(child_)).join("");
}

export function ezMarkdownParser(markdown_) {
  const safeMarkdown = typeof markdown_ === "string" ? markdown_ : "";
  const blockTokens = tokenize(safeMarkdown);
  const documentTree = buildDocumentTree(blockTokens);
  pruneLineBreakNodes(documentTree);
  return renderNode(documentTree);
}

function pruneLineBreakNodes(node_) {
  if (!node_ || !Array.isArray(node_.children)) {
    return;
  }
  const isParagraph = node_.type === NodeType.PARAGRAPH;
  node_.children = node_.children
    .map((child_) => {
      pruneLineBreakNodes(child_);
      return child_;
    })
    .filter((child_) => {
      if (!child_) {
        return false;
      }
      if (child_.type === NodeType.LINE_BREAK && !isParagraph) {
        return false;
      }
      return true;
    });
}
